import React, { createContext, useContext, useCallback, useEffect } from "react";
import { BI_SERVICE_HOST } from "../setting";
import { useFirebaseToken } from "../firebase/FirebaseProvider";

interface UTMParams {
    ad_id?: string;
    adset_id?: string;
    campaign_id?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_source?: string;
}

interface EmailParams {
    email: string;
}

type BIServiceContextValue = {
    firstLaunch: () => void;
    email: (email: string) => void;
};

const BIServiceContext = createContext<BIServiceContextValue>({
    firstLaunch: () => {},
    email: () => {},
});

export const BIServiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { token } = useFirebaseToken();

    // Сохраняем UTM параметры при создании компонента (ДО любых редиректов)
    const [initialUTMParams] = React.useState(() => {
        const currentSearch = window.location.search;

        // Проверяем, есть ли уже сохраненные параметры
        const savedParams = sessionStorage.getItem('utm_params');
        if (savedParams) {
            return savedParams;
        }

        // Если параметры есть в текущем URL, сохраняем их
        if (currentSearch) {
            const searchParams = new URLSearchParams(currentSearch);
            const hasUTMParams = Array.from(searchParams.keys()).some(key =>
                key.startsWith('utm_') || key === 'ad_id' || key === 'adset_id' || key === 'campaign_id'
            );

            if (hasUTMParams) {
                sessionStorage.setItem('utm_params', currentSearch);
                return currentSearch;
            }
        }

        return '';
    });

    const firstLaunch = useCallback(() => {
        if (!token) return;

        // Извлекаем query параметры из сохраненных или текущего URL
        const searchParams = new URLSearchParams(initialUTMParams || window.location.search);

        const utmParams: UTMParams = {
            ad_id: searchParams.get("ad_id") || undefined,
            adset_id: searchParams.get("adset_id") || undefined,
            campaign_id: searchParams.get("campaign_id") || undefined,
            utm_campaign: searchParams.get("utm_campaign") || undefined,
            utm_content: searchParams.get("utm_content") || undefined,
            utm_source: searchParams.get("utm_source") || undefined,
        };

        // Отправляем только если есть хотя бы один параметр
        const hasParams = Object.values(utmParams).some(value => value !== undefined);
        if (!hasParams) return;

        // Fire-and-forget запрос
        fetch(`${BI_SERVICE_HOST}/api/v1/tracking/utm`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(utmParams),
        }).then(() => {
            // Очищаем сохраненные параметры после успешной отправки
            sessionStorage.removeItem('utm_params');
        }).catch((error) => {
            // Молча игнорируем ошибки, т.к. это fire-and-forget
            console.error("BI tracking error (utm):", error);
        });
    }, [token, initialUTMParams]);

    const email = useCallback((userEmail: string) => {
        if (!token || !userEmail) return;

        const emailParams: EmailParams = {
            email: userEmail,
        };

        // Fire-and-forget запрос
        fetch(`${BI_SERVICE_HOST}/api/v1/tracking/email`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(emailParams),
        }).catch((error) => {
            // Молча игнорируем ошибки, т.к. это fire-and-forget
            console.error("BI tracking error (email):", error);
        });
    }, [token]);

    // Автоматически вызываем firstLaunch при запуске приложения
    useEffect(() => {
        if (token) {
            firstLaunch();
        }
    }, [token, firstLaunch]);

    return (
        <BIServiceContext.Provider value={{ firstLaunch, email }}>
            {children}
        </BIServiceContext.Provider>
    );
};

export const useBIService = () => useContext(BIServiceContext);
